/**
 * Table de correspondance unique entre le nom de l'application (tel qu'annoncé
 * par Capacitor ou transmis dans les liens) et sa marque : hôte web dédié,
 * raccourci de lien profond, liens de téléchargement et logos.
 *
 * L'hôte dédié est essentiel : `core.stryvfit.app` est revendiqué par toutes
 * les applications installées, donc un lien central peut ouvrir la mauvaise
 * application. Chaque hôte ci-dessous n'est revendiqué que par son app.
 */
export interface AppBranding {
  key: string;
  longName: string;
  host: string;
  deepLinkShortcut: string;
  /** Clé courte transmise dans les liens génériques (`?app=`). */
  linkKey: string;
  androidPackage: string;
  logoLightUrl: string;
  logoDarkLightUrl: string;
  appleStoreLink: string;
  playStoreLink: string;
}

export const DEFAULT_APP_HOST = "core.stryvfit.app";

const BRANDINGS: AppBranding[] = [
  {
    key: "VB20TN",
    longName: "Vision Body Tunisie",
    host: "app.vb20tunisie.com",
    deepLinkShortcut: "vb20tn",
    linkKey: "vb20tn",
    androidPackage: "com.fintexia.vb20",
    logoLightUrl: "/lovable-uploads/vb-logo-white.png",
    logoDarkLightUrl: "/lovable-uploads/vb-logo-dark.png",
    appleStoreLink: "https://apps.apple.com/app/id6745799782",
    playStoreLink: "https://play.google.com/store/apps/details?id=com.fintexia.vb20",
  },
  {
    key: "Maison Pilates",
    longName: "Maison Pilates",
    host: "mpilates.stryvfit.app",
    deepLinkShortcut: "mpilates",
    linkKey: "mpilates",
    androidPackage: "com.fintexia.mpilate",
    logoLightUrl: "/lovable-uploads/maison-pilates-logo-white.png",
    logoDarkLightUrl: "/lovable-uploads/maison-pilates-logo-dark.png",
    appleStoreLink: "https://apps.apple.com/app/id6754303636",
    playStoreLink: "https://play.google.com/store/apps/details?id=com.fintexia.mpilate",
  },
  {
    key: "StriFit",
    longName: "StriFit",
    host: DEFAULT_APP_HOST,
    deepLinkShortcut: "stryvfit",
    linkKey: "stryvfit",
    androidPackage: "com.fintexia.stryvfit",
    logoLightUrl: "/lovable-uploads/strifit-logo-white.png",
    logoDarkLightUrl: "/lovable-uploads/strifit-logo-dark.png",
    appleStoreLink: "",
    playStoreLink: "https://play.google.com/store/apps/details?id=com.fintexia.stryvfit",
  },
];

/** Écritures alternatives rencontrées (nom natif Capacitor, variantes libres). */
const ALIASES: Record<string, string> = {
  vb20tn: "VB20TN",
  vb20: "VB20TN",
  "vb20 tn": "VB20TN",
  "vision body tunisie": "VB20TN",
  "visionbody": "VB20TN",
  "maison pilates": "Maison Pilates",
  mpilates: "Maison Pilates",
  "maisonpilates": "Maison Pilates",
  strifit: "StriFit",
  stryvfit: "StriFit",
  "stryv fit": "StriFit",
};

const normalize = (value?: string | null): string =>
  (value ?? "").trim().replace(/\s+/g, " ").toLowerCase();

export const DEFAULT_APP_BRANDING = BRANDINGS[2];

export const resolveAppBranding = (appName?: string | null): AppBranding => {
  const normalized = normalize(appName);
  if (!normalized) return DEFAULT_APP_BRANDING;

  const aliasKey = ALIASES[normalized];
  const match = BRANDINGS.find(
    (branding) => normalize(branding.key) === normalized || branding.key === aliasKey
  );
  return match ?? DEFAULT_APP_BRANDING;
};

/** Nom canonique attendu par `app_gym_assignments` et les e-mails. */
export const canonicalAppName = (appName?: string | null): string =>
  resolveAppBranding(appName).key;

export const appBaseUrlFor = (appName?: string | null): string =>
  `https://${resolveAppBranding(appName).host}`;

/** Domaine générique utilisé par tous les liens envoyés par e-mail. */
export const GENERIC_LINK_BASE_URL = `https://${DEFAULT_APP_HOST}`;

/** Lit l'app depuis les paramètres de lien (`app` prioritaire, `appName` hérité). */
export const appFromSearchParams = (params: URLSearchParams): AppBranding =>
  resolveAppBranding(params.get("app") || params.get("appName"));
